// Fachlogik des Abwägens ohne Oberfläche. Herausgelöst aus AbwaegenStudio.jsx,
// AbwaegenPunktKarte.jsx und abwaegenAnzeige.jsx (23.09.2026), damit Desktop
// und Handy dieselben Adressen, Nutzlasten, Zähler und Texte benutzen.
//
// Die Regeln dahinter stehen in AGENTS.md ("Abwägen: Offen · Übernommen ·
// Antwort"): Übernommenes ändert die KI nie mehr, Kommentare gehen gesammelt
// mit der nächsten Auswertung an sie.

// Was die Ladeanzeige während einer Auswertung durchlaufen lässt.
export const ABWAEGEN_SCHRITTE = [
  'Zerlegt die Wünsche in Punkte …',
  'Prüft jeden Punkt am Vertrag …',
  'Wägt wirtschaftlich und für Investoren ab …',
  'Formuliert die Antworten …',
];

export const DEAL_ERKLAERUNG = 'Freiwillig. Mit Deal kommen die Eckdaten aus Bitrix dazu, und die Antwort lässt sich dort als Kommentar ablegen. Ohne Deal ist die Abwägung nur für dich sichtbar.';

export const KOMMENTAR_PLATZHALTER = 'Was soll die KI ändern? Etwa: freundlicher formulieren, Notfallzugang erwähnen …';

export const STELLE_GEPRUEFT = 'Die Stelle steht wörtlich so im Vertrag.';
export const STELLE_UNGEPRUEFT = 'Das Zitat der KI ließ sich im Vertrag nicht bestätigen.';
export const STELLE_NACHSEHEN = 'Der Wortlaut ließ sich im Vertrag nicht bestätigen — bitte dort nachsehen.';

export const EMPFEHLUNG_TEXT = {
  zustimmen: 'Zustimmen',
  bedingung: 'Mit Bedingung',
  ablehnen: 'Ablehnen',
};

export const LEERE_ABWAEGUNG = { verlauf: [], bewertung: null, fazit: '', fazitAus: [], bitrixGesendetAm: null };

// ---------- Adresse und Nutzlast ----------

/** Mit Deal dessen Nummer, ohne Deal die persönliche Sitzung (Kennung vergibt der Server). */
export function abwaegenSitzungsParameter(dealId) {
  return dealId ? `vorgang=${encodeURIComponent(dealId)}` : 'ohne_vorgang=1';
}

export function abwaegenAdresse(apiBase, dealId) {
  return `${apiBase}/contracts/state?resource=verhandlung&${abwaegenSitzungsParameter(dealId)}`;
}

/** Der Körper jedes POST: Vorgang oder "ohne Vorgang", Vertragsart, dann die Nutzlast. */
export function abwaegenKoerper({ dealId, vertrag }, nutzlast) {
  return { ...(dealId ? { vorgang: dealId } : { ohneVorgang: true }), contractKey: vertrag || undefined, ...nutzlast };
}

/** Eine Deal-Nummer besteht nur aus Ziffern (höchstens zwölf). */
export function istDealNummer(text) {
  return /^\d{1,12}$/.test(String(text ?? '').trim());
}

// ---------- Punkte und Kommentare ----------

/** Setzt den Stand eines Punkts lokal - für die sofortige Anzeige. */
export function mitPunktStand(sitzung, punktId, status) {
  const bewertung = sitzung?.abwaegung?.bewertung;
  if (!bewertung) return sitzung;
  return {
    ...sitzung,
    abwaegung: {
      ...sitzung.abwaegung,
      bewertung: {
        ...bewertung,
        punkte: bewertung.punkte.map((p) => (p.id === punktId
          ? { ...p, status, ...(status === 'uebernommen' ? { kommentar: '' } : {}) }
          : p)),
      },
    },
  };
}

/** { offen, uebernommen } - Übernommenes ist abgestimmt, alles andere offen. */
export function teilePunkte(punkte) {
  const liste = Array.isArray(punkte) ? punkte : [];
  return {
    offen: liste.filter((p) => p.status !== 'uebernommen'),
    uebernommen: liste.filter((p) => p.status === 'uebernommen'),
  };
}

/** Nur die getippten Kommentare zu Punkten, die noch offen sind. */
export function offeneKommentarEntwuerfe(punkte, entwuerfe) {
  const offen = new Set((punkte || []).filter((p) => p.status !== 'uebernommen').map((p) => p.id));
  return Object.fromEntries(Object.entries(entwuerfe || {}).filter(([id]) => offen.has(id)));
}

/** Wie viele offene Punkte einen Kommentar tragen - getippt oder gespeichert. */
export function zaehleKommentare(punkte, entwuerfe) {
  const getippt = entwuerfe || {};
  return (punkte || [])
    .filter((p) => p.status !== 'uebernommen' && String(getippt[p.id] ?? p.kommentar ?? '').trim())
    .length;
}

/** Was im Gespräch steht, solange die KI auswertet. */
export function unterwegsText(frage, kommentare) {
  const zahl = Object.values(kommentare || {}).filter((k) => String(k).trim()).length;
  return frage || (zahl ? `Kommentare zu ${zahl === 1 ? 'einem Punkt' : `${zahl} Punkten`}` : '');
}

/**
 * Nach einem Fehler: Kam die Nachricht samt Kommentaren noch an und scheiterte
 * erst die KI, steht sie als letzter Beitrag im Verlauf. Dann darf nichts
 * zurück ins Feld - sonst ginge alles doppelt.
 */
export function nachrichtAngekommen(stand, frage, kommentare) {
  const letzter = stand?.abwaegung?.verlauf?.at(-1);
  return letzter?.rolle === 'mensch'
    && [frage, ...Object.values(kommentare || {})].every((t) => !String(t).trim() || letzter.text.includes(String(t).trim()));
}

/** Der Hinweis unter dem Eingabefeld - nur, wenn er gerade etwas Neues sagt. */
export function hinweisZumSenden({ kommentarZahl, hatText, letzterVonMensch }) {
  // Ohne eigenen Text sendet der Knopf unter den Punkten - hier nichts doppelt.
  if (kommentarZahl && hatText) return `${kommentarZahl === 1 ? 'Dein Kommentar geht' : `Deine ${kommentarZahl} Kommentare gehen`} mit`;
  if (letzterVonMensch && !hatText) return 'Enter wertet das Angehängte aus';
  return '';
}

/** Die Antwort an den Kunden entstand aus einer anderen Auswahl als der jetzigen. */
export function antwortVeraltet(fazit, fazitAus, uebernommen) {
  if (!fazit || !Array.isArray(fazitAus) || fazitAus.length === 0) return false;
  const jetzt = (uebernommen || []).map((p) => p.id).sort().join('|');
  return jetzt !== [...fazitAus].sort().join('|');
}

/**
 * Der Antworttext im Feld, wenn vom Server ein anderes fazit kommt.
 *
 * Das Feld folgt dem Server, wenn der Vorgang gewechselt hat oder wenn es
 * seit dem zuletzt übernommenen fazit nicht verändert wurde. Eine
 * ungespeicherte Änderung bleibt stehen: Ein bloßes Neuladen (etwa nach einem
 * erneuerten Anmeldetoken oder "Erneut laden") darf sie nicht überschreiben
 * (Review 23.09.2026).
 *
 * @param {{ entwurf: string, bisherigesFazit: string, neuesFazit: string, vorgangGewechselt: boolean }} stand
 * @returns {string}
 */
export function naechsterAntwortEntwurf({ entwurf, bisherigesFazit, neuesFazit, vorgangGewechselt }) {
  const neu = neuesFazit || '';
  if (vorgangGewechselt) return neu;
  if ((entwurf || '') === (bisherigesFazit || '')) return neu;
  return entwurf;
}

/**
 * Bewertungen von vor dem 23.09.2026 haben keine Antwort. Dann steht die alte
 * Einschätzung da, damit der Punkt nicht leer wirkt. Leer, wenn es eine
 * Antwort oder einen Gegenvorschlag gibt.
 */
export function ersatzAussage(punkt) {
  if (!punkt || punkt.antwort || punkt.gegenvorschlag) return '';
  const alt = punkt.formal
    ? punkt.wirtschaftlich?.text
    : [punkt.wirtschaftlich?.text, punkt.verkaeuflich?.text].filter(Boolean).join(' ');
  return alt || '';
}

// ---------- Einschätzung und Belege ----------

// Die Farbe kommt aus dem Urteil, nicht aus der Spalte (abwaegenAnzeige.jsx).
// Erlaubte Werte laut api/_lib/abwaegungsPrompt.js:
//   wirtschaftlich: unproblematisch | vertretbar | teuer   | unklar
//   verkaeuflich:   unproblematisch | vertretbar | kritisch | unklar
const URTEIL_TON = {
  unproblematisch: 'gut',
  vertretbar: 'info',
  teuer: 'gefahr',
  kritisch: 'gefahr',
  unklar: 'neutral',
};

/** 'gut' | 'info' | 'gefahr' | 'neutral' */
export function urteilsTon(bewertung) {
  const schluessel = String(bewertung || '').toLowerCase();
  return Object.prototype.hasOwnProperty.call(URTEIL_TON, schluessel) ? URTEIL_TON[schluessel] : 'neutral';
}

// Herkunft und Belastbarkeit werden mit angezeigt, statt still zu filtern:
// Der Vertrag gilt, das Schulungsmaterial ist nachrangig.
export const BELASTBARKEIT_TEXT = {
  1: 'Vertrag',
  2: 'Vorgabe / geprüft',
  3: 'Schulungsmaterial',
};

export function belegArt(treffer) {
  return BELASTBARKEIT_TEXT[treffer?.belastbarkeit] || treffer?.gattung || 'unbekannt';
}

/** Belastbarkeit 1 ist der Vertrag selbst - der wiegt schwerer als alles andere. */
export function istAusDemVertrag(treffer) {
  return treffer?.belastbarkeit === 1 || Boolean(treffer?.vertragsgeprueft);
}

/** Die Belege zu einem Punkt: über die Kennung, bei alten Einträgen über die Nummer. */
export function belegZu(belege, punkt) {
  return (belege || []).find((b) => (b?.id ? b.id === punkt?.id : String(b?.nummer ?? '') === String(punkt?.nummer ?? '')));
}
